import React, { useEffect, useRef, useState } from "react";
import { Share, Plus, X } from "lucide-react";
import { surfaceStyle, surfaceClass } from "@/shared/motion/surface";

/**
 * Mobile PWA "Save to Home Screen" prompt.
 *
 * Behavior rules:
 *  - Only shows on mobile devices and when not already installed.
 *  - Android Chrome/Edge: uses the native `beforeinstallprompt` event.
 *  - iOS Safari: fallback instructional card (Share → Add to Home Screen).
 *  - Waits for the first user interaction (tap/scroll/keypress) then a short
 *    delay so we never interrupt the first moments of the page.
 *  - Dismissal sets a 7-day cooldown in localStorage — we don't nag.
 *  - "appinstalled" event immediately hides + marks installed.
 */

const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const INTERACTION_DELAY_MS = 10_000;         // ms after first interaction — give users time to use the form
const FALLBACK_DELAY_MS = 45_000;            // if no interaction at all

const DISMISS_KEY = "link_pwa_dismissed_until";
const INSTALLED_KEY = "link_pwa_installed";

const isStandaloneDisplay = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true); // iOS Safari

const isIOS = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const plat = typeof navigator !== "undefined" ? navigator.platform : "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (plat === "MacIntel" && (navigator.maxTouchPoints || 0) > 1) // iPadOS masquerades
  );
};

const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini|Mobile Safari/i.test(
    navigator.userAgent
  );
};

const isInCooldown = () => {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch {
    return false;
  }
};

export default function InstallPrompt() {
  const [mode, setMode] = useState(null); // "android" | "ios" | null
  const [deferred, setDeferred] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const deferredRef = useRef(null);
  const scheduledRef = useRef(false);
  const showTimerRef = useRef(null);

  // Hide the prompt whenever the user is typing in a form control.
  // We attach at the window level so it works across every page.
  useEffect(() => {
    const isFormControl = (el) =>
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
    const onFocusIn = (e) => { if (isFormControl(e.target)) setInputFocused(true); };
    const onFocusOut = () => {
      // small timeout to catch tab-between-inputs without flickering the prompt
      setTimeout(() => {
        const a = document.activeElement;
        setInputFocused(!!(a && isFormControl(a)));
      }, 100);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  useEffect(() => {
    // Never show in standalone (already installed) or on desktop
    if (isStandaloneDisplay()) {
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
      return;
    }
    if (!isMobile()) return;
    if (isInCooldown()) return;

    const scheduleShow = (desiredMode, delay) => {
      if (scheduledRef.current) return;
      scheduledRef.current = true;
      showTimerRef.current = setTimeout(() => {
        if (!isStandaloneDisplay() && !isInCooldown()) setMode(desiredMode);
      }, delay);
    };

    // Android / Chromium — wait for the native event, then arm the prompt
    const onBefore = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferred(e);
      scheduleShow("android", INTERACTION_DELAY_MS);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
      clearTimeout(showTimerRef.current);
      setMode(null);
      setDeferred(null);
      deferredRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback: no beforeinstallprompt on Safari. Arm the fallback on
    // first interaction — gives the user a moment to engage with the page.
    let onInteract = null;
    if (isIOS()) {
      onInteract = () => {
        scheduleShow("ios", INTERACTION_DELAY_MS);
        window.removeEventListener("pointerdown", onInteract);
        window.removeEventListener("keydown", onInteract);
        window.removeEventListener("scroll", onInteract);
      };
      window.addEventListener("pointerdown", onInteract, { passive: true });
      window.addEventListener("keydown", onInteract);
      window.addEventListener("scroll", onInteract, { passive: true });
      // Safety net: show after FALLBACK_DELAY_MS even without interaction.
      showTimerRef.current = setTimeout(() => {
        if (!isStandaloneDisplay() && !isInCooldown()) setMode("ios");
      }, FALLBACK_DELAY_MS);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (onInteract) {
        window.removeEventListener("pointerdown", onInteract);
        window.removeEventListener("keydown", onInteract);
        window.removeEventListener("scroll", onInteract);
      }
      clearTimeout(showTimerRef.current);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS)); } catch {}
    setMode(null);
  };

  const installAndroid = async () => {
    const evt = deferredRef.current || deferred;
    if (!evt) return dismiss();
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
      } else {
        // user said not now — respect cooldown
        try { localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS)); } catch {}
      }
    } catch (err) {
      console.warn("PWA install prompt failed:", err?.message || err);
    }
    setMode(null);
    deferredRef.current = null;
    setDeferred(null);
  };

  if (!mode || inputFocused) return null;

  return (
    <div
      className={`fixed inset-x-3 sm:inset-x-auto sm:right-5 bottom-20 md:bottom-6 sm:w-[380px] z-[70] bg-[#0A1128]/88 text-[#F9F7F4] p-5 sm:p-6 rounded-2xl ${surfaceClass()}`}
      style={surfaceStyle("card", { theme: "dark" })}
      role="dialog"
      aria-labelledby="pwa-install-title"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <img src="/assets/link-symbol.svg?v=13" alt="" aria-hidden="true" className="h-9 w-9 sm:h-10 sm:w-10 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-xs text-[#5c97f7] mb-1.5">Link app</div>
          <div id="pwa-install-title" className="font-serif text-xl sm:text-2xl leading-tight mb-1.5">
            Add Link to your Home Screen
          </div>
          <div className="text-sm text-[#F9F7F4]/80 leading-snug mb-4">
            Get faster access and a smoother experience.
          </div>

          {mode === "ios" && (
            <div
              className="bg-[#F9F7F4]/10 border border-[#F9F7F4]/15 p-3 sm:p-4 rounded-none text-[13px] sm:text-sm leading-relaxed text-[#F9F7F4]/90 space-y-2 mb-4"
              data-testid="pwa-ios-instructions"
            >
              <div className="flex items-center gap-2">
                <span className="label-xs text-[#ed6500]">Step 1</span>
                <span>Tap the</span>
                <Share size={15} className="inline-block mx-1" strokeWidth={1.7} />
                <span>Share icon</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="label-xs text-[#ed6500]">Step 2</span>
                <span>Choose</span>
                <span className="inline-flex items-center gap-1 font-medium">
                  <Plus size={14} strokeWidth={1.7} /> Add to Home Screen
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {mode === "android" && (
              <button
                onClick={installAndroid}
                className="bg-[#ed6500] hover:bg-[#d55b00] text-white px-4 py-2.5 text-[11px] tracking-widest uppercase font-semibold inline-flex items-center gap-2"
                data-testid="pwa-install-btn"
              >
                <Plus size={14} strokeWidth={1.8} /> Add to Home Screen
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-[11px] tracking-widest uppercase text-[#F9F7F4]/70 hover:text-[#F9F7F4] px-3 py-2.5"
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="text-[#F9F7F4]/60 hover:text-[#F9F7F4] p-1 -mr-1 -mt-1"
          data-testid="pwa-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
